import { motion } from 'framer-motion';
import { ArrowRight, Clock, ShoppingCart, Users, ScanBarcode } from 'lucide-react';

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-50 via-white to-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            <div className="inline-flex items-center gap-2 bg-brand-primary/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-functional-success rounded-full animate-pulse-soft"></span>
              <span className="text-brand-primary font-semibold text-sm">Skip the queue, shop smarter</span>
            </div>
            
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] text-brand-secondary mb-6">
              Scan. Pay.{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-accent">
                Walk out.
              </span>
            </h1>
            
            <p className="text-lg md:text-xl font-medium leading-relaxed text-neutral-text-secondary mb-8 max-w-lg">
              No more waiting in checkout lines. Let your customers scan product barcodes 
              with their phone and pay instantly. The future of retail is queue-free.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8 py-4 font-bold shadow-[0_4px_14px_0_rgba(99,91,255,0.39)] transition-all hover:scale-105 active:scale-95"
                data-testid="hero-cta-button"
              >
                Get GrabPe for your store
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-brand-primary hover:bg-brand-primary/10 rounded-full px-8 py-4 font-semibold transition-all border border-brand-primary/20"
                data-testid="hero-secondary-btn"
              >
                See how it works
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2 text-neutral-text-secondary">
                <Clock className="w-5 h-5 text-functional-success" />
                <span className="text-sm font-medium">Save 10+ mins per shop</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-text-secondary">
                <ShoppingCart className="w-5 h-5 text-functional-warning" />
                <span className="text-sm font-medium">Works in any store</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-text-secondary">
                <Users className="w-5 h-5 text-brand-accent" />
                <span className="text-sm font-medium">500K+ happy shoppers</span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex justify-center lg:justify-end"
          >
            {/* Phone Frame */}
            <div className="relative animate-float">
              <div className="relative w-[280px] md:w-[320px] h-[560px] md:h-[640px] bg-brand-secondary rounded-[48px] p-3 shadow-2xl">
                {/* Phone Screen */}
                <div className="w-full h-full bg-white rounded-[40px] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-brand-secondary rounded-b-3xl z-10"></div>
                  
                  {/* Screen Content */}
                  <div className="h-full pt-10 flex flex-col">
                    {/* App Header */}
                    <div className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center">
                            <ScanBarcode className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-heading font-bold text-brand-secondary">GrabPe</span>
                        </div>
                        <div className="bg-functional-success/20 text-functional-success px-3 py-1 rounded-full text-xs font-semibold">
                          Shopping
                        </div>
                      </div>
                    </div>

                    {/* Scanner View */}
                    <div className="flex-1 flex flex-col items-center justify-center px-6 bg-gradient-to-b from-slate-900 to-slate-800 mx-4 rounded-2xl relative overflow-hidden">
                      {/* Scanner Frame */}
                      <div className="w-48 h-32 border-2 border-brand-primary rounded-xl relative">
                        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-brand-accent"></div>
                        <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-brand-accent"></div>
                        <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-brand-accent"></div>
                        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-brand-accent"></div>
                        
                        {/* Scan Line Animation */}
                        <motion.div
                          className="absolute left-0 right-0 h-0.5 bg-brand-primary"
                          animate={{ top: ['10%', '90%', '10%'] }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                        />
                      </div>
                      <p className="text-white/80 text-sm mt-4 font-medium">Point at product barcode</p>
                    </div>

                    {/* Cart Summary */}
                    <div className="px-6 py-4 bg-neutral-bg-secondary">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-neutral-text-secondary">Cart (3 items)</span>
                        <span className="text-xs text-brand-primary font-semibold">View all</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-neutral-text-secondary">Total</p>
                          <p className="text-2xl font-heading font-bold text-brand-secondary">$47.85</p>
                        </div>
                        <button className="bg-brand-primary text-white px-4 py-2 rounded-full text-sm font-bold">
                          Pay Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                className="absolute -left-8 top-20 bg-white rounded-2xl p-4 shadow-lg border border-neutral-border"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-functional-success/20 rounded-full flex items-center justify-center">
                    <ScanBarcode className="w-5 h-5 text-functional-success" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-text-secondary">Item added</p>
                    <p className="font-semibold text-brand-secondary text-sm">Organic Milk</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                className="absolute -right-8 bottom-32 bg-white rounded-2xl p-4 shadow-lg border border-neutral-border"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-primary/20 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-text-secondary">Time saved</p>
                    <p className="font-semibold text-functional-success text-sm">12 minutes!</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-brand-primary/5 to-transparent pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-brand-accent/5 to-transparent pointer-events-none"></div>
    </section>
  );
};

export default Hero;
